<template>
  <div class="pa-4">
    <v-data-table
      :headers="headers"
      :items="items"
      class="elevation-1"
      :search="search"
      sort-by="receipt_no"
      :sort-desc="true"
    >
      <!-- ═══════════════════════ TOOLBAR ═══════════════════════ -->
      <template v-slot:top>
        <v-toolbar flat>
          <v-toolbar-title>In Stock Bahan Baku</v-toolbar-title>
          <v-divider class="mx-4" inset vertical></v-divider>
          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            label="Search"
            single-line
            hide-details
          ></v-text-field>
          <v-spacer></v-spacer>

          <v-btn color="primary" class="mr-2" @click="initialize">
            Refresh
          </v-btn>
          <v-btn
            color="primary"
            dark
            @click="openAddDialog"
            v-if="$store.state.currentPagePriv.create"
          >
            Add New
          </v-btn>

          <!-- ─── Add / Edit / View Dialog ─── -->
          <v-dialog v-model="dialog" v-if="dialog" max-width="800px">
            <v-card>
              <v-card-title class="d-flex align-center">
                <span class="text-h6">{{ formTitle }}</span>
                <v-spacer></v-spacer>
                <!-- Approval Status Badge shown in view mode -->
                <template v-if="viewMode && editedIndex > -1">
                  <span class="mr-2 body-2 grey--text">Approval Status:</span>
                  <v-chip
                    :color="getStatusColor(editedItem.status)"
                    dark
                    small
                    label
                  >
                    {{ getStatusLabel(editedItem.status) }}
                  </v-chip>
                </template>
              </v-card-title>
              <v-divider></v-divider>

              <v-card-text>
                <v-container>
                  <v-form ref="form" v-model="valid" lazy-validation>
                    <v-row>
                      <!-- Nomor Penerimaan -->
                      <v-col cols="12" sm="6" md="6" v-if="editedIndex > -1">
                        <span class="caption">Nomor Penerimaan</span>
                        <v-text-field
                          v-model="editedItem.receipt_no"
                          filled rounded dense hide-details="auto"
                          readonly
                        ></v-text-field>
                      </v-col>

                      <!-- Tanggal Penerimaan -->
                      <v-col cols="12" sm="6" md="6">
                        <span class="caption">Tanggal Penerimaan</span>
                        <div v-if="!viewMode && !isReadonlyByStatus">
                          <v-menu
                            v-model="datepicker"
                            :close-on-content-click="false"
                            :nudge-right="40"
                            transition="scale-transition"
                            offset-y
                            min-width="auto"
                          >
                            <template v-slot:activator="{ on, attrs }">
                              <v-text-field
                                v-model="editedItem.receipt_date"
                                filled rounded dense hide-details="auto"
                                :rules="reqRule"
                                prepend-icon="mdi-calendar"
                                readonly
                                v-bind="attrs"
                                v-on="on"
                              ></v-text-field>
                            </template>
                            <v-date-picker
                              v-model="editedItem.receipt_date"
                              @input="datepicker = false"
                            ></v-date-picker>
                          </v-menu>
                        </div>
                        <div v-else>
                          <v-text-field
                            filled rounded dense hide-details="auto"
                            :value="formatDate(editedItem.receipt_date)"
                            prepend-icon="mdi-calendar"
                            readonly
                          ></v-text-field>
                        </div>
                      </v-col>

                      <!-- Kode Bahan (view mode only) -->
                      <v-col cols="12" sm="6" md="6" v-if="viewMode">
                        <span class="caption">Kode Bahan</span>
                        <v-text-field
                          :value="editedItem.material && editedItem.material.code || ''"
                          filled rounded dense hide-details="auto"
                          readonly
                        ></v-text-field>
                      </v-col>

                      <!-- Bahan Baku -->
                      <v-col cols="12" sm="6" md="6">
                        <span class="caption">Bahan Baku</span>
                        <v-autocomplete
                          filled rounded dense
                          :items="rawmaterials"
                          item-text="description"
                          item-value="code"
                          v-model="editedItem.material.code"
                          :readonly="viewMode || isReadonlyByStatus"
                          :rules="reqRule"
                          hide-details="auto"
                        ></v-autocomplete>
                      </v-col>

                      <!-- Supplier -->
                      <v-col cols="12" sm="6" md="6">
                        <span class="caption">Supplier</span>
                        <v-autocomplete
                          filled rounded dense
                          :items="suppliers"
                          item-text="supplier_name"
                          item-value="code"
                          v-model="editedItem.supplier.code"
                          :readonly="viewMode || isReadonlyByStatus"
                          :rules="reqRule"
                          hide-details="auto"
                        ></v-autocomplete>
                      </v-col>

                      <!-- Total Penerimaan -->
                      <v-col cols="12" sm="6" md="6">
                        <span class="caption">Total Penerimaan Supplier</span>
                        <v-text-field
                          type="number"
                          v-model="editedItem.total_qty"
                          filled rounded dense hide-details="auto"
                          :readonly="viewMode || isReadonlyByStatus"
                          :rules="reqRule"
                          :suffix="fillUom(editedItem.material && editedItem.material.code) || ''"
                          @wheel="$event.target.blur()"
                        ></v-text-field>
                      </v-col>

                      <!-- Stock Status (view mode) -->
                      <v-col cols="12" sm="6" md="6" v-if="viewMode">
                        <span class="caption">Stock Status</span>
                        <v-text-field
                          :value="editedItem.stock_updated ? 'Updated' : ''"
                          filled rounded dense hide-details="auto"
                          readonly
                        ></v-text-field>
                      </v-col>

                      <!-- Total Defect (view mode) -->
                      <v-col cols="12" sm="6" md="6" v-if="viewMode">
                        <span class="caption">Total Defect</span>
                        <v-text-field
                          :value="getTotalDefectDisplay()"
                          filled rounded dense hide-details="auto"
                          readonly
                        ></v-text-field>
                      </v-col>

                      <v-col cols="12" v-if="viewMode">
                        <span class="caption">Catatan Approval</span>
                        <v-textarea
                          :value="editedItem.approval_note"
                          filled rounded dense hide-details="auto"
                          readonly
                          rows="3"
                          no-resize
                        ></v-textarea>
                      </v-col>
                    </v-row>
                  </v-form>
                </v-container>
              </v-card-text>
              <v-divider></v-divider>

              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="blue darken-1" text @click="close">
                  {{ viewMode ? 'Close' : 'Cancel' }}
                </v-btn>
                <v-btn
                  color="blue darken-1"
                  text
                  @click="save"
                  v-if="!viewMode"
                >
                  Save
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>

          <!-- ─── Approve / Reject Confirmation Dialog ─── -->
          <v-dialog v-model="dialogApproval" max-width="420px">
            <v-card>
              <v-card-title class="text-h6 justify-center pt-6">
                {{ approvalAction === 'approve' ? 'Approve Data?' : 'Reject Data?' }}
              </v-card-title>
              <v-card-text class="text-center pb-0">
                <p class="body-2 mb-4">
                  {{ approvalAction === 'approve'
                      ? 'Yakin Menyetujui Request ini?'
                      : 'Yakin Menolak Request ini?' }}
                </p>
                <v-textarea
                  v-model="approvalNote"
                  label="Tulis alasan (opsional)"
                  outlined
                  dense
                  rows="3"
                  hide-details
                ></v-textarea>
              </v-card-text>
              <v-card-actions class="justify-center py-4">
                <v-btn
                  color="primary"
                  class="mr-2"
                  @click="confirmApproval"
                  :loading="onProgress"
                >
                  Ya
                </v-btn>
                <v-btn
                  color="error"
                  @click="dialogApproval = false"
                >
                  Tidak
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>

          <!-- ─── Cancel (Undo Stock) Dialog ─── -->
          <v-dialog v-model="dialogCancel" max-width="500px">
            <v-card>
              <v-card-title class="body-1 text-center d-block pt-5">
                Proses ini akan mengurangi quantity stock pada Bahan Baku ini.
                Anda yakin akan membatalkan transaksi in-stock ini?
              </v-card-title>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="blue darken-1" text @click="closeDelete">No</v-btn>
                <v-btn color="blue darken-1" text @click="cancelItemConfirm">Yes</v-btn>
                <v-spacer></v-spacer>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-toolbar>
      </template>

      <!-- ═══════════════════════ STATUS COLUMN ═══════════════════════ -->
      <!-- Plain colored text — matches wireframe (no chip in table) -->
      <template v-slot:[`item.status`]="{ item }">
        <span :class="getStatusTextClass(item.status)" class="font-weight-medium">
          {{ getStatusLabel(item.status) }}
        </span>
      </template>

      <!-- ═══════════════════════ QTY COLUMNS ═══════════════════════ -->
      <template v-slot:[`item.total_qty`]="{ item }">
        {{ item.total_qty }} {{ item.material.uom }}
      </template>

      <template v-slot:[`item.total_defect_qty`]="{ item }">
        {{ item.total_defect_qty }} {{ item.material.uom }}
      </template>

      <template v-slot:[`item.stock_updated`]="{ item }">
        {{ item.stock_updated ? 'Updated' : '' }}
      </template>

      <!-- ═══════════════════════ ACTIONS COLUMN ═══════════════════════ -->
      <template v-slot:[`item.actions`]="{ item }">

        <!-- ── ADMIN ROLE ── -->
        <template v-if="isAdmin">

          <!-- ✓ Approve (pending only) -->
          <v-tooltip bottom v-if="$store.state.currentPagePriv.update">
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                small class="mr-1" color="success"
                v-show="!item.status || item.status === 'pending'"
                @click="openApprovalDialog(item, 'approve')"
                v-bind="attrs" v-on="on"
              >mdi-check</v-icon>
            </template>
            <span>Approve</span>
          </v-tooltip>

          <!-- ✗ Reject (pending only) -->
          <v-tooltip bottom v-if="$store.state.currentPagePriv.update">
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                small class="mr-1" color="error"
                v-show="!item.status || item.status === 'pending'"
                @click="openApprovalDialog(item, 'reject')"
                v-bind="attrs" v-on="on"
              >mdi-close</v-icon>
            </template>
            <span>Reject</span>
          </v-tooltip>

          <!-- 👁 View (always) -->
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                small class="mr-1"
                @click="viewItem(item)"
                v-bind="attrs" v-on="on"
              >mdi-eye</v-icon>
            </template>
            <span>View</span>
          </v-tooltip>

          <!-- ✏ Edit (pending only) -->
          <v-tooltip bottom v-if="$store.state.currentPagePriv.update">
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                small class="mr-1"
                v-show="item.status !== 'rejected'"
                @click="editItem(item)"
                v-bind="attrs" v-on="on"
              >mdi-pencil</v-icon>
            </template>
            <span>Edit</span>
          </v-tooltip>

          <!-- ⊘ Cancel Stock (approved + stock updated) -->
          <v-tooltip bottom v-if="$store.state.currentPagePriv.delete">
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                small class="mr-1"
                v-show="item.status !== 'rejected'"
                @click="cancelItem(item)"
                v-bind="attrs" v-on="on"
              >mdi-cancel</v-icon>
            </template>
            <span>Cancel</span>
          </v-tooltip>

          <!-- ➕ Defect Stock (approved + stock updated) -->
          <v-tooltip bottom v-if="$store.state.currentPagePriv.update">
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                small
                v-show="item.status !== 'rejected'"
                @click="defectStock(item)"
                v-bind="attrs" v-on="on"
              >mdi-plus-box</v-icon>
            </template>
            <span>Defect Stock</span>
          </v-tooltip>
        </template>

        <!-- ── USER ROLE ── -->
        <template v-else>

          <!-- 👁 View (always) -->
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                small class="mr-1"
                @click="viewItem(item)"
                v-bind="attrs" v-on="on"
              >mdi-eye</v-icon>
            </template>
            <span>View</span>
          </v-tooltip>

          <!-- ✏ Edit (pending only — user can edit before admin reviews) -->
          <v-tooltip bottom v-if="$store.state.currentPagePriv.update">
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                small class="mr-1"
                v-show="item.status === 'pending'"
                @click="editItem(item)"
                v-bind="attrs" v-on="on"
              >mdi-pencil</v-icon>
            </template>
            <span>Edit</span>
          </v-tooltip>

          <!-- ⊘ Cancel Stock -->
          <v-tooltip bottom v-if="$store.state.currentPagePriv.delete">
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                small class="mr-1"
                v-show="item.stock_updated"
                @click="cancelItem(item)"
                v-bind="attrs" v-on="on"
              >mdi-cancel</v-icon>
            </template>
            <span>Cancel</span>
          </v-tooltip>

          <!-- ➕ Defect Stock -->
          <v-tooltip bottom v-if="$store.state.currentPagePriv.update">
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                small
                v-show="item.stock_updated"
                @click="defectStock(item)"
                v-bind="attrs" v-on="on"
              >mdi-plus-box</v-icon>
            </template>
            <span>Defect Stock</span>
          </v-tooltip>
        </template>
      </template>

      <!-- No Data -->
      <template v-slot:no-data>
        <v-btn color="primary" @click="initialize">Refresh</v-btn>
      </template>
    </v-data-table>

    <defect ref="defectRef" @refresh="initialize"></defect>

    <v-overlay :value="onProgress" z-index="999">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </div>
</template>

<script>
import Defect from '../components/Defect.vue'

export default {
  components: { Defect },

  data: () => ({
    dialog: false,
    dialogCancel: false,
    dialogApproval: false,
    approvalAction: 'approve',  // 'approve' | 'reject'
    approvalNote: '',
    viewMode: false,
    datepicker: false,
    search: '',
    valid: true,
    onProgress: false,

    headers: [
      { text: 'Nomor Penerimaan', value: 'receipt_no' },
      { text: 'Tanggal',          value: 'receipt_date' },
      { text: 'Kode Bahan',       value: 'material.code' },
      { text: 'Nama Bahan',       value: 'material.description' },
      { text: 'Supplier',         value: 'supplier.supplier_name' },
      { text: 'Total Penerimaan', value: 'total_qty',         align: 'center' },
      { text: 'Stok Status',      value: 'stock_updated',     align: 'center' },
      { text: 'Total Defect',     value: 'total_defect_qty',  align: 'center' },
      { text: 'Status',           value: 'status',   align: 'center' },
      { text: 'Action',           value: 'actions', sortable: false, align: 'center' },
    ],

    items: [],
    suppliers: [],
    categories: [],
    rawmaterials: [],
    editedIndex: -1,

    defaultItem: {
      material: {},
      supplier: {},
      status: 'pending',
      receipt_date: (new Date(Date.now() - (new Date()).getTimezoneOffset() * 60000))
        .toISOString().substr(0, 10),
    },
    editedItem: {
      material: {},
      supplier: {},
      status: 'pending',
      receipt_date: (new Date(Date.now() - (new Date()).getTimezoneOffset() * 60000))
        .toISOString().substr(0, 10),
    },

    reqRule: [v => !!v || 'Harus diisi!'],
  }),

  computed: {
    formTitle() {
      if (this.editedIndex === -1) return 'New In-Stock Bahan Baku'
      return this.viewMode ? 'View In-Stock Bahan Baku' : 'Edit In-Stock Bahan Baku'
    },

    profile() {
      return this.$store.state.profile
    },

    // SUP = superadmin/admin yang bisa approve & reject
    isAdmin() {
      return this.$store.state.profile && this.$store.state.profile.role_code === 'SUP'
    },

    /**
     * Edit fields are locked when the record is no longer 'pending'.
     * Approved / rejected records should not be editable.
     */
    isReadonlyByStatus() {
      return this.editedItem.status !== 'pending'
    },
  },

  watch: {
    dialog(val)       { val || this.close() },
    dialogCancel(val) { val || this.closeDelete() },
  },

  created() {
    this.initialize()
  },

  methods: {
    // ─── Initialise ─────────────────────────────────────────────────────
    async initialize() {
      this.onProgress = true
      try {
        this.suppliers    = (await this.$store.dispatch('supplier/getObjs')).data.data
        this.categories   = (await this.$store.dispatch('category/getObjs')).data.data
        this.rawmaterials = (await this.$store.dispatch('rawmaterial/getObjs')).data.data
        const res = await this.$store.dispatch('getObjs', { apidomain: 'inStocks', parameter: '' })
        if (res.data.status === 'success') this.items = res.data.data
      } finally {
        this.onProgress = false
      }
    },

    // ─── Open Dialogs ───────────────────────────────────────────────────
    openAddDialog() {
      this.editedIndex = -1
      this.editedItem  = Object.assign({}, this.defaultItem)
      this.viewMode    = false
      this.dialog      = true
    },

    viewItem(item) {
      this.editedIndex = this.items.indexOf(item)
      this.editedItem  = Object.assign({}, item)
      this.viewMode    = true
      this.dialog      = true
    },

    editItem(item) {
      this.editedIndex = this.items.indexOf(item)
      this.editedItem  = Object.assign({}, item)
      this.viewMode    = false
      this.dialog      = true
    },

    cancelItem(item) {
      this.editedIndex  = this.items.indexOf(item)
      this.editedItem   = Object.assign({}, item)
      this.dialogCancel = true
    },

    defectStock(obj) {
      this.$refs.defectRef.openDialog(obj)
    },

    openApprovalDialog(item, action) {
      this.editedIndex    = this.items.indexOf(item)
      this.editedItem     = Object.assign({}, item)
      this.approvalAction = action
      this.approvalNote   = ''
      this.dialogApproval = true
    },

    // ─── Close Dialogs ──────────────────────────────────────────────────
    close() {
      this.dialog = false
      this.$nextTick(() => {
        this.editedItem  = Object.assign({}, this.defaultItem)
        this.editedIndex = -1
        this.viewMode    = false
      })
    },

    closeDelete() {
      this.dialogCancel = false
      this.$nextTick(() => {
        this.editedItem  = Object.assign({}, this.defaultItem)
        this.editedIndex = -1
      })
    },

    // ─── Save (Create / Update) ─────────────────────────────────────────
    async save() {
      await this.$refs.form.validate()
      if (!this.valid) return

      this.onProgress = true
      try {
        const payload = {
          receipt_date:    this.editedItem.receipt_date,
          total_qty:       this.editedItem.total_qty,
          material_code:   this.editedItem.material.code,
          supplier_code:   this.editedItem.supplier.code,
          stock_updated:   true,
          // New records always start as 'pending'; edits keep existing status
          status: this.editedIndex === -1 ? 'pending' : this.editedItem.status,
        }

        if (this.editedIndex > -1) {
          // UPDATE existing record
          const url = this.$store.state.config.apiHost + 'inStocks/' + this.editedItem.receipt_no
          const res = await this.$axios.put(url, JSON.stringify(payload), {
            headers: {
              'Authorization': 'Bearer ' + this.$store.state.token,
              'Content-Type': 'application/json',
            },
          })
          if (res.data.status === 'success') {
            this.$nextTick(() => Object.assign(this.items[this.editedIndex], res.data.data))
          }
        } else {
          // CREATE new record
          const url = this.$store.state.config.apiHost + 'inStocks'
          const res = await this.$axios.post(url, JSON.stringify(payload), {
            headers: {
              'Authorization': 'Bearer ' + this.$store.state.token,
              'Content-Type': 'application/json',
            },
          })
          if (res.data.status === 'success') {
            this.items.push(res.data.data)
          }
        }
        this.close()
      } finally {
        this.onProgress = false
      }
    },

    // ─── Approve / Reject ───────────────────────────────────────────────
    async confirmApproval() {
      this.onProgress = true
      try {
        const isApprove = this.approvalAction === 'approve'
        const payload   = {
          status: isApprove ? 'approved' : 'rejected',
          approval_note:   this.approvalNote,
          // Stock is marked updated on approve; keep existing value on reject
          stock_updated:   isApprove ? true : this.editedItem.stock_updated,
        }

        const url = this.$store.state.config.apiHost + 'inStocks/' + this.editedItem.receipt_no
        const res = await this.$axios.put(url, JSON.stringify(payload), {
          headers: {
            'Authorization': 'Bearer ' + this.$store.state.token,
            'Content-Type': 'application/json',
          },
        })

        if (res.data.status === 'success') {
          Object.assign(this.items[this.editedIndex], res.data.data)
        }
        this.dialogApproval = false
      } finally {
        this.onProgress = false
      }
    },

    // ─── Cancel / Undo Stock ────────────────────────────────────────────
    async cancelItemConfirm() {
      this.onProgress = true
      try {
        const payload = { stock_updated: false }
        const url     = this.$store.state.config.apiHost + 'inStocks/' + this.editedItem.receipt_no
        const res     = await this.$axios.put(url, JSON.stringify(payload), {
          headers: {
            'Authorization': 'Bearer ' + this.$store.state.token,
            'Content-Type': 'application/json',
          },
        })
        if (res.data.status === 'success') {
          Object.assign(this.items[this.editedIndex], res.data.data)
        }
        this.closeDelete()
      } finally {
        this.onProgress = false
      }
    },

    // ─── Helpers ────────────────────────────────────────────────────────
    fillUom(raw_material_code) {
      if (!raw_material_code) return ''
      const m = this.rawmaterials.find(e => e.code === raw_material_code)
      return m ? m.uom : ''
    },

    getTotalDefectDisplay() {
      const code      = this.editedItem.material && this.editedItem.material.code
      const uom       = this.fillUom(code) || ''
      const defectQty = this.editedItem.total_defect_qty || 0
      return defectQty + ' ' + uom
    },

    formatDate(date) {
      if (!date) return ''
      const d = new Date(date)
      return isNaN(d) ? date : d.toLocaleDateString()
    },

    /**
     * Admin (SUP) → pending tampil "Request Baru" sesuai wireframe
     * User biasa  → pending tampil "Pending"
     */
    getStatusLabel(status) {
      const isAdmin = this.$store.state.profile && this.$store.state.profile.role_code === 'SUP'
      const map = {
        pending:  isAdmin ? 'Request Baru' : 'Pending',
        approved: 'Disetujui',
        rejected: 'Ditolak',
      }
      return map[status] || (isAdmin ? 'Request Baru' : 'Pending')
    },

    /** Color for v-chip (used in dialog header badge) */
    getStatusColor(status) {
      const map = { pending: 'orange', approved: 'success', rejected: 'error' }
      return map[status] || 'grey'
    },

    /** CSS class for plain colored text in the table status column */
    getStatusTextClass(status) {
      const map = {
        pending:  'orange--text text--darken-1',
        approved: 'green--text text--darken-1',
        rejected: 'red--text text--darken-1',
      }
      return map[status] || 'orange--text text--darken-1'
    },

    getCategoryName(code) {
      const f = this.categories.find(e => e.code === code)
      return f ? f.category_name : ''
    },

    getRawMaterialName(code) {
      const f = this.rawmaterials.find(e => e.code === code)
      return f ? f.description : ''
    },

    getSupplierName(code) {
      if (!code) return ''
      const f = this.suppliers.find(e => e.code === code)
      return f ? f.supplier_name : ''
    },
  },
}
</script>

<style>
.v-text-field fieldset,
.v-text-field .v-input__control,
.v-text-field .v-input__slot {
  border-radius: 7px !important;
}
</style>